import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MediaFrame from './MediaFrame';

const SolutionPanel = ({ solution }) => {
  const panelRef = useRef(null);
  const imageRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (isMobile || prefersReducedMotion) {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.25,
        rootMargin: '-10% 0px'
      }
    );

    const panelElement = panelRef.current;
    if (panelElement) {
      observer.observe(panelElement);
    }

    return () => {
      if (panelElement) {
        observer.unobserve(panelElement);
      }
    };
  }, []);

  useEffect(() => {
    const panelElement = panelRef.current;
    const imageElement = imageRef.current;
    if (!panelElement || !imageElement) return undefined;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isMobile || prefersReducedMotion) {
      imageElement.style.transform = 'none';
      return undefined;
    }

    imageElement.style.willChange = 'transform';

    let frameId = null;
    let isAnimating = false;
    let currentY = 0;
    let currentScale = 1.06;
    let targetY = 0;
    let targetScale = 1.06;

    const applyTransform = () => {
      imageElement.style.transform = `translate3d(0, ${currentY}px, 0) scale(${currentScale})`;
    };

    const updateTargets = () => {
      const rect = panelElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const progress = Math.min(
        1,
        Math.max(0, (viewportHeight - rect.top) / (viewportHeight + rect.height))
      );
      const centerBias = 1 - Math.abs(progress - 0.5) * 2;
      targetY = (0.5 - progress) * 80;
      targetScale = 1.06 + centerBias * 0.08;
    };

    const animate = () => {
      currentY += (targetY - currentY) * 0.1;
      currentScale += (targetScale - currentScale) * 0.1;
      applyTransform();

      const yDone = Math.abs(targetY - currentY) < 0.08;
      const scaleDone = Math.abs(targetScale - currentScale) < 0.0008;
      if (yDone && scaleDone) {
        currentY = targetY;
        currentScale = targetScale;
        applyTransform();
        isAnimating = false;
        frameId = null;
        return;
      }

      frameId = window.requestAnimationFrame(animate);
    };

    const requestAnimation = () => {
      updateTargets();
      if (isAnimating) return;
      isAnimating = true;
      frameId = window.requestAnimationFrame(animate);
    };

    requestAnimation();
    window.addEventListener('scroll', requestAnimation, { passive: true });
    window.addEventListener('resize', requestAnimation);

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', requestAnimation);
      window.removeEventListener('resize', requestAnimation);
      imageElement.style.willChange = '';
      imageElement.style.transform = '';
    };
  }, [solution.image]);

  useEffect(() => {
    const mediaElement = imageRef.current;
    if (!mediaElement || typeof HTMLVideoElement === 'undefined') return undefined;
    if (!(mediaElement instanceof HTMLVideoElement)) return undefined;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const p = mediaElement.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } else if (!mediaElement.paused) {
          mediaElement.pause();
        }
      },
      { threshold: 0.35 }
    );

    obs.observe(mediaElement);
    return () => obs.disconnect();
  }, [solution.image]);

  const handleExploreClick = (e) => {
    e.preventDefault();
    setIsTransitioning(true);
    setTimeout(() => {
      navigate(`/solutions/${solution.id}`);
    }, 600);
  };

  const renderCopy = (variant) => (
    <>
      <span className="inline-block text-sm font-semibold text-teal-300 mb-3 tracking-wider uppercase">
        {solution.eyebrow}
      </span>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 leading-tight">
        {solution.title}
      </h2>
      <p className="text-lg lg:text-2xl text-slate-200 font-medium mb-4">
        {solution.subtitle}
      </p>
      <p className="text-sm sm:text-base lg:text-lg text-slate-300 mb-6 leading-relaxed max-w-3xl">
        {solution.description}
      </p>
      {solution.detailPage && (
        <div className="mt-6">
          <button
            onClick={handleExploreClick}
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/30 hover:-translate-y-0.5"
          >
            Explore More
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </>
  );

  return (
    <section ref={panelRef} className="bg-slate-950 border-t border-slate-800/40 relative z-10">
      <div className="w-full">
        <div className="relative w-full">
          <MediaFrame
            ref={imageRef}
            src={solution.image}
            alt={solution.title}
            fit="contain"
          />

          <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-slate-950/75 via-slate-950/30 to-transparent pointer-events-none" />
          <div className="hidden lg:block absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent pointer-events-none" />

          <div
            className={`hidden lg:flex absolute inset-0 z-10 items-end transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="max-w-[920px] p-8 lg:p-14">
              {renderCopy('overlay')}
            </div>
          </div>
        </div>

        <div
          className={`lg:hidden bg-slate-950 px-6 py-8 sm:px-8 sm:py-10 transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {renderCopy('stacked')}
        </div>
      </div>

      {/* Page transition overlay */}
      <div
        className={`fixed inset-0 z-[100] bg-slate-950 pointer-events-none transition-opacity duration-600 ${
          isTransitioning ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDuration: '600ms' }}
      />
    </section>
  );
};

export default SolutionPanel;
